import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ReactComponent as ProgramIcon } from '../../assets/svg/ProgramIcon.svg'
import { ReactComponent as SurveyContentIcon } from '../../assets/svg/SurveyContentIcon.svg'
import { ReactComponent as JournalContentIcon } from '../../assets/svg/JournalContentIcon.svg'
import { ReactComponent as SearchMagnifyIcon } from '../../assets/svg/SearchMagnifyIcon.svg'
import { useTranslation } from 'react-i18next'
import SingleProgram from './ToGo/SingleProgram'
import SingleSurvey from './ToGo/SingleSurvey'
import SingleJournal from './ToGo/SingleJournal'
import RunningProgram from './ToGo/RunningProgram'
import ContentTopicButtons from './ContentTopicButtons'
import TypeHomeList from './TypeHomeList'
import TopicHomeList from './TopicHomeList'
import { usePageTitle } from '../../utils/util'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'
import useReadyToGoContent, { filterList, filterTopicContent } from './useReadyToGoContent'

const LIBRARY_TYPE_EVENTS = {
    Programs: ANALYTICS_EVENTS.CONTENT_LIBRARY_PROGRAMS_VIEWED,
    Surveys: ANALYTICS_EVENTS.CONTENT_LIBRARY_SURVEYS_VIEWED,
    Journals: ANALYTICS_EVENTS.CONTENT_LIBRARY_JOURNALS_VIEWED,
}

const CONTENT_LIST = [
    {
        name: 'Programs',
        description: 'Choose from our range of ready-to-go programs designed to support your students and save you time',
        icon: <ProgramIcon />,
    },
    {
        name: 'Surveys',
        description: 'Each of our ready-to-go surveys is designed to help you understand your students better',
        icon: <SurveyContentIcon />,
    },
    {
        name: 'Journals',
        description: 'Pick a journal that will keep your students engaged',
        icon: <JournalContentIcon />,
    },
]

const MODES = CONTENT_LIST.map((c) => c.name)
const DEFAULT_MODE = 'Programs'
const DEFAULT_TOPIC = 'generalWellbeing'

const readModeFromSearch = (search) => {
    const requested = new URLSearchParams(search).get('mode')
    return MODES.includes(requested) ? requested : DEFAULT_MODE
}

function ReadyToGo() {
    usePageTitle('Upstrive Education - Ready-to-go content')
    const history = useHistory()
    const location = useLocation()
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const [mode, setMode] = useState(() => readModeFromSearch(location.search))
    const [contentGroup, setContentGroup] = useState('type')
    const [selectedTopic, setSelectedTopic] = useState(DEFAULT_TOPIC)
    const [searchTerm, setSearchTerm] = useState('')
    const [screen, setScreen] = useState('home')
    // { kind: 'Programs' | 'Surveys' | 'Journals', item } — kind is stored with
    // the item so opening from the topic view never has to change `mode`.
    const [selected, setSelected] = useState(null)
    const [selectedRunningProgram, setSelectedRunningProgram] = useState([])

    useTrackedSurface(contentGroup === 'topic' ? ANALYTICS_EVENTS.CONTENT_LIBRARY_TOPIC_VIEWED : ANALYTICS_EVENTS.CONTENT_LIBRARY_TYPE_VIEWED)
    useTrackedSurface(LIBRARY_TYPE_EVENTS[mode], {
        active: screen === 'home' && contentGroup === 'type',
    })
    useTrackedSurface(ANALYTICS_EVENTS.CONTENT_LIBRARY_CATEGORY_VIEWED, {
        active: screen === 'home' && contentGroup === 'topic',
        contentId: selectedTopic,
        content: t(selectedTopic),
    })

    // Keep `?mode=` in sync so refresh / back keeps the selected tab.
    useEffect(() => {
        const params = new URLSearchParams(location.search)
        if (params.get('mode') === mode) return
        params.set('mode', mode)
        history.replace({ pathname: location.pathname, search: `?${params.toString()}` })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode])

    const typeSource = useReadyToGoContent({ kind: mode, locale, enabled: contentGroup === 'type' })
    const topicSource = useReadyToGoContent({ kind: 'topic', param: selectedTopic, locale, enabled: contentGroup === 'topic' })

    const normalizedTerm = searchTerm.trim().toLowerCase()
    const typeItems = useMemo(() => filterList(typeSource.data, normalizedTerm), [typeSource.data, normalizedTerm])
    const topicItems = useMemo(() => filterTopicContent(topicSource.data, normalizedTerm), [topicSource.data, normalizedTerm])

    const openCurrentlyRunningPrograms = useCallback((runningPrograms) => {
        setSelectedRunningProgram((runningPrograms || []).map((item) => item.id))
        setScreen('manage')
    }, [])

    const handleManageRunningPrograms = () => {
        setSelectedRunningProgram([])
        setScreen((prev) => (prev === 'manage' ? 'home' : 'manage'))
    }

    const selectType = () => {
        setContentGroup('type')
        setScreen('home')
    }
    const selectTopic = () => {
        setContentGroup('topic')
        setSelectedTopic(DEFAULT_TOPIC)
        setSearchTerm('')
        setScreen('home')
    }
    const selectMode = (nextMode) => {
        setMode(nextMode)
        setScreen('home')
        setSearchTerm('')
    }

    const openItem = useCallback((item, kind) => {
        setSelected({ kind, item })
        setScreen('single')
        setSearchTerm('')
    }, [])

    const goHome = useCallback(() => setScreen('home'), [])

    const renderSingle = () => {
        if (!selected) return null
        const { kind, item } = selected
        if (kind === 'Journals' || item?.type === 'journal') return <SingleJournal journal={item} setScreen={goHome} />
        if (kind === 'Surveys') return <SingleSurvey surveyID={item?.id} setScreen={goHome} />
        return <SingleProgram programID={item?.id} setScreen={goHome} />
    }

    const showManageButton = contentGroup === 'topic' || mode === 'Programs'

    return (
        <div className="bg-lightBG w-full h-full pt-12 flex flex-col overflow-y-scroll">
            <div className="relative h-max flex justify-center items-center">
                <div className="bg-white w-[1200px] rounded-xl flex flex-col px-[50px] py-[40px] ">
                    <div className="flex flex-row items-end">
                        <div className="flex-1">
                            <h1 className="text-[40px] text-darkenPurple font-bold">{t('readyToGoContent')} </h1>
                        </div>

                        <div className="flex flex-row items-center">
                            <span className="text-[#9392AD] text-[18px] font-semibold mr-[14px]">{t('sortBy')} :</span>
                            <div className="rtg-toggle bg-[#F8F8FB] h-[50px] items-center justify-center w-[220px] rounded-[100px] flex flex-row" data-active={contentGroup}>
                                <div className="rtg-toggle-pill" aria-hidden="true" />
                                <button
                                    type="button"
                                    aria-pressed={contentGroup === 'type'}
                                    className={`rtg-toggle-button flex-1 font-bold text-[18px] rounded-[100px] h-[50px] ${contentGroup === 'type' ? 'text-[#FDDF6D]' : 'text-[#9392AD]'}`}
                                    onClick={selectType}
                                >
                                    {t('type')}
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={contentGroup === 'topic'}
                                    className={`rtg-toggle-button flex-1 font-bold text-[18px] rounded-[100px] h-[50px] ${contentGroup === 'topic' ? 'text-[#FDDF6D]' : 'text-[#9392AD]'}`}
                                    onClick={selectTopic}
                                >
                                    {t('topic')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <p className="text-base text-mudPurple">{t('evidenceBasedContentPreparedForYouByOurTeamOfExperts')} </p>
                    {contentGroup === 'type' && (
                        <div className="flex flex-row justify-between mt-8">
                            {CONTENT_LIST.map((content) => {
                                const isSelected = mode === content.name
                                return (
                                    <button
                                        type="button"
                                        id={`ready-to-go-content-button-${content.name}`}
                                        className="rtg-mode-button relative flex justify-center"
                                        aria-pressed={isSelected}
                                        onClick={() => selectMode(content.name)}
                                        key={content.name}
                                    >
                                        <div
                                            className={`rtg-mode-card p-7 pointer rounded-xl max-w-[340px] flex flex-col border-2 border-[#5E639B] h-full ${
                                                isSelected ? 'bg-[#5E639B]' : 'bg-white hover:bg-lightBG'
                                            }`}
                                        >
                                            <div className="flex flex-row justify-start items-center">
                                                {React.cloneElement(content.icon, { fill: isSelected ? '#FDDF6D' : '#5E639B' })}
                                                <span className={`ml-3 font-bold text-xl ${isSelected ? 'text-[#FDDF6D]' : 'text-deepPurple'}`}>{t(content.name)}</span>
                                            </div>
                                            <p className={`text-base text-left mt-4 ${isSelected ? 'text-white' : 'text-darkenPurple'}`}>{t(content.description)}</p>
                                        </div>
                                        {isSelected && (
                                            <div
                                                className="rtg-mode-arrow w-0 h-0 absolute"
                                                style={{
                                                    borderRight: '20px solid transparent',
                                                    borderLeft: '20px solid transparent',
                                                    borderTop: '20px solid #5E639B',
                                                    bottom: -20,
                                                }}
                                            ></div>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    )}
                    {contentGroup === 'topic' && <ContentTopicButtons selectTopic={setSelectedTopic} selectedTopic={selectedTopic} />}
                    <div className="w-full flex flex-row items-center justify-center mt-[40px] pt-[25px]" style={{ borderTop: '1px dashed #BEBECE' }}>
                        <div className="rtg-search w-[341px] h-[50px] flex flex-row border border-deselectPurple rounded-full items-center pr-[17px]">
                            <input
                                id="ready-to-go-search-input"
                                type="text"
                                placeholder={t('search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full h-full p-4 flex-1 rounded-full"
                                autoComplete="off"
                            />
                            <button
                                type="button"
                                className="rtg-search-clear"
                                data-visible={searchTerm.length > 0}
                                aria-label={t('Clear')}
                                tabIndex={searchTerm.length > 0 ? 0 : -1}
                                onClick={() => setSearchTerm('')}
                            >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                                    <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                            <SearchMagnifyIcon />
                        </div>
                        <div className="flex-1"></div>
                        {showManageButton && (
                            <button
                                type="button"
                                id="manage-running-programs-button"
                                aria-pressed={screen === 'manage'}
                                className={`rtg-manage-button pointer flex items-center justify-center font-semibold text-lg rounded-full h-[50px] w-[334px] ${
                                    screen === 'manage'
                                        ? 'bg-deepPurple text-white hover:text-highlightColor'
                                        : 'bg-white border border-[#BEBECE] text-[#BEBECE] hover:border-[#5E639B] hover:text-[#5E639B]'
                                }`}
                                onClick={handleManageRunningPrograms}
                            >
                                {t('manageRunningPrograms')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* Keyed on `screen` so each panel swap fades in; mode/topic changes
                inside the home screen animate at the list level instead. */}
            <div className="w-full flex justify-center mt-[30px] rtg-panel-enter" key={screen}>
                {screen === 'single' && <div className="bg-white w-[1200px] rounded-xl flex flex-col h-max">{renderSingle()}</div>}
                {screen === 'home' && contentGroup === 'type' && (
                    <TypeHomeList
                        content={typeItems}
                        type={mode}
                        onClick={openItem}
                        openCurrentlyRunningPrograms={openCurrentlyRunningPrograms}
                        loading={typeSource.loading}
                        version={typeSource.version}
                    />
                )}
                {screen === 'home' && contentGroup === 'topic' && (
                    <TopicHomeList
                        content={topicItems}
                        onClick={openItem}
                        openCurrentlyRunningPrograms={openCurrentlyRunningPrograms}
                        loading={topicSource.loading}
                        version={topicSource.version}
                    />
                )}
                {screen === 'manage' && <RunningProgram selectedRunningProgram={selectedRunningProgram} />}
            </div>
        </div>
    )
}

export default ReadyToGo
